/**
 * Display-name resolution for users with optional preferred first / last names.
 *
 * Rule (per-field fallback):
 *   displayFirst = preferredFirstName (non-blank) ?? firstName
 *   displayLast  = preferredLastName  (non-blank) ?? lastName
 *
 * The legacy single-field `preferredName` column is intentionally NOT read here —
 * it is no longer displayed anywhere and is only surfaced on admin pages so it can
 * be migrated by hand into the two new fields.
 *
 * The SQL equivalent lives in therapi
 * (src/db/migrations/20261010_preferred_first_last_name.sql):
 *   coalesce(nullif(trim(preferred_first_name), ''), first_name)
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();export type NameParts = {
  firstName?: string | null
  lastName?: string | null
  preferredFirstName?: string | null
  preferredLastName?: string | null
}

const clean = (value: unknown): string =>
  typeof value === "string" ? value.trim() : ""

const pick = (preferred: unknown, legal: unknown): string => {
  const preferredClean = clean(preferred)
  return preferredClean || clean(legal)
}

export const resolveDisplayFirstName = (parts: NameParts | null | undefined) =>
  pick(parts?.preferredFirstName, parts?.firstName)

export const resolveDisplayLastName = (parts: NameParts | null | undefined) =>
  pick(parts?.preferredLastName, parts?.lastName)

export const resolveFullDisplayName = (parts: NameParts | null | undefined) =>
  [resolveDisplayFirstName(parts), resolveDisplayLastName(parts)]
    .filter(Boolean)
    .join(" ")

/** True when the user has set at least one preferred-name field. */
export const hasPreferredName = (parts: NameParts | null | undefined) =>
  Boolean(clean(parts?.preferredFirstName) || clean(parts?.preferredLastName))

type ProviderPrefixed = {
  providerFirstName?: string | null
  providerLastName?: string | null
  providerPreferredFirstName?: string | null
  providerPreferredLastName?: string | null
}

/** Adapter for rows shaped like search_groups_public / list_organization_groups. */
export const providerNameParts = (
  row: ProviderPrefixed | null | undefined,
): NameParts => ({
  firstName: row?.providerFirstName,
  lastName: row?.providerLastName,
  preferredFirstName: row?.providerPreferredFirstName,
  preferredLastName: row?.providerPreferredLastName,
})

type PrimaryProviderPrefixed = {
  primaryProviderFirstName?: string | null
  primaryProviderLastName?: string | null
  primaryProviderPreferredFirstName?: string | null
  primaryProviderPreferredLastName?: string | null
}

/** Adapter for rows shaped like public_group_detail / public_group_detail_by_uuid. */
export const primaryProviderNameParts = (
  row: PrimaryProviderPrefixed | null | undefined,
): NameParts => ({
  firstName: row?.primaryProviderFirstName,
  lastName: row?.primaryProviderLastName,
  preferredFirstName: row?.primaryProviderPreferredFirstName,
  preferredLastName: row?.primaryProviderPreferredLastName,
})
